import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { getTodoById, getToDoStatus, UpdateToDo } from 'core/API/todo';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Divider,
    Button,
    Paper,
    Grid,
    TextField,
} from '@mui/material';
import styles from '../addToDo/AddToDo.module.scss';
import { formatDateMMDDYYYY, formatTime12Hour } from 'shared/utils/dateUtils';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useWorkplaceUser } from 'module/documents/utils';
import { useToDoPermissions } from 'module/toDoPage/hooks/useToDoPermissions';
import { parsePriorityIdFromRef } from 'core/utils/todoScheduleEvent';
import {
    findDoneStatusId,
    isDoneStatusId,
    normalizeTodoStatusList,
} from 'module/toDoPage/utils/todoCompletionUtils';
import { buildTodoUpdatePayloadFromRecord } from 'module/toDoPage/utils/todoUpdatePayload';
import { resolveTodoReturnPath } from 'module/toDoPage/utils/todoNavigation';
import { getCurrentWorkplaceId } from 'core/session/workplaceStore';

type RouteParams = {
    id: string;
};

const getRawDueTime = (todo: any): string | undefined => {
    return todo?.dueTime ?? todo?.due_time ?? undefined;
};

const computeDueTimeDisplay = (todo: any): string => {
    const dueTime = getRawDueTime(todo);
    if (typeof dueTime === 'string' && dueTime.trim() !== '') {
        return formatTime12Hour(dueTime);
    }
    const dueDate = getRawDueDate(todo);
    if (typeof dueDate === 'string' && dueDate.includes('T')) {
        const match = dueDate.match(/T(\d{2}:\d{2}(?::\d{2})?)/);
        if (match?.[1]) return formatTime12Hour(match[1]);
    }
    return '--:--';
};

const getRawDueDate = (todo: any): string | undefined => {
    return (
        todo?.due_date ??
        todo?.dueDate ??
        todo?.due_at ??
        todo?.dueOn ??
        todo?.due
    );
};

const GetSingleTodo = () => {
    const { id } = useParams<RouteParams>();
    const history = useHistory();
    const location = useLocation();
    const returnTo = resolveTodoReturnPath(location.state);
    const queryClient = useQueryClient();
    const { showToast } = useToastContext();
    const workplace = useWorkplaceUser();
    const workplaceId = getCurrentWorkplaceId() || workplace.data.id;
    const userId = Number(localStorage.getItem('userId')) || (workplace as any)?.data?.user?.id || 0;
    const { canComplete: canExecuteToDo } = useToDoPermissions();

    const {
        data: todo,
        isLoading,
        error,
    } = useQuery(['todo', id], () => getTodoById(Number(id)), {
        enabled: !!id,
        refetchOnWindowFocus: false,
    });

    const { data: statusList } = useQuery(
        ['todo-statuses', workplaceId],
        () => getToDoStatus(),
        {
            enabled: !!workplaceId,
            refetchOnWindowFocus: false,
        }
    );

    const normalizedStatuses = normalizeTodoStatusList(statusList);
    const currentStatusId = parsePriorityIdFromRef(todo?.status) ?? 0;
    const isTodoDone = isDoneStatusId(currentStatusId, normalizedStatuses);

    const { mutate: markComplete, isLoading: isMarkingComplete } = useMutation(
        (payload: Record<string, unknown>) => UpdateToDo(Number(id), payload),
        {
            onSuccess: () => {
                showToast('success', 'ToDo marked as complete!');
                queryClient.invalidateQueries(['todos-categorized', workplaceId]);
                queryClient.invalidateQueries(['todo', id]);
                history.push(returnTo);
            },
            onError: (updateError) => {
                console.error('Failed to mark todo complete:', updateError);
                showToast('error', 'Failed to mark ToDo as complete');
            },
        }
    );

    const handleMarkComplete = () => {
        const doneStatusId = findDoneStatusId(normalizedStatuses);
        if (!doneStatusId) {
            showToast('error', 'Done status is not configured');
            return;
        }

        if (!todo) {
            showToast('error', 'ToDo not found');
            return;
        }

        markComplete(
            buildTodoUpdatePayloadFromRecord(todo, doneStatusId, {
                workplaceId,
                currentUserId: userId,
                statuses: normalizedStatuses,
            })
        );
    };

    if (isLoading) {
        return (
            <div style={{
                width: "100%",
                height: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            }}>
                <CircularProgress />
            </div>
        );
    }

    if (error) {
        return (
            <Box mt={4}>
                <Alert severity="error">Failed to load ToDo details.</Alert>
            </Box>
        );
    }

    const assignedType = todo?.assignedGroup ? 'Group' : 'Person';
    const assignedValue = todo?.assignedGroup
        ? (todo.assignedGroup?.label || todo.assignedGroup?.name || '')
        : (todo?.user ? `${todo.user?.firstName || ''} ${todo.user?.lastName || ''}`.trim() : '');
    const categoryValue = todo?.category?.label || todo?.category?.name || todo?.category?.title || '';

    const rawDueDate = getRawDueDate(todo);
    const readonlyInputSx = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#fff',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0,0,0,0.23)',
        },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0,0,0,0.23)',
        },
    } as const;

    return (
        <Paper elevation={1} style={{ padding: '24px', maxWidth: '100%', margin: '0 auto' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Button
                    style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white', padding: '2px 12px' }}
                    onClick={() => history.push(returnTo)}
                >
                    Back
                </Button>
                {!isTodoDone && canExecuteToDo && (
                    <Button
                        style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white', padding: '5px 12px' }}
                        disabled={isMarkingComplete}
                        onClick={handleMarkComplete}
                    >
                        {isMarkingComplete ? 'Marking Complete...' : 'Mark as Complete'}
                    </Button>
                )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="h6" mb={2} className={styles.schedulerTitle}>
                View To-Do
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <TextField
                        label="Assignment Type"
                        value={assignedType}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        sx={readonlyInputSx}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label={assignedType === 'Person' ? 'Selected Person' : 'Selected Group'}
                        value={assignedValue}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        sx={readonlyInputSx}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label="Description"
                        fullWidth
                        value={todo?.description || ''}
                        InputProps={{ readOnly: true }}
                        sx={readonlyInputSx}
                    />
                </Grid>

                <Grid item xs={6}>
                    <TextField
                        label="Due Date"
                        fullWidth
                        value={formatDateMMDDYYYY(rawDueDate)}
                        InputProps={{ readOnly: true }}
                        sx={readonlyInputSx}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="Due Time"
                        fullWidth
                        value={computeDueTimeDisplay(todo)}
                        InputProps={{ readOnly: true }}
                        sx={readonlyInputSx}
                    />
                </Grid>

                <Grid item xs={6}>
                    <TextField
                        label="Priority"
                        fullWidth
                        value={todo?.priority?.label || todo?.priority?.name || ''}
                        InputProps={{ readOnly: true }}
                        sx={readonlyInputSx}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="Status"
                        fullWidth
                        value={todo?.status?.label || todo?.status?.name || ''}
                        InputProps={{ readOnly: true }}
                        sx={readonlyInputSx}
                    />
                </Grid>

                <Grid item xs={6}>
                    <TextField
                        label="Category"
                        fullWidth
                        value={categoryValue}
                        InputProps={{ readOnly: true }}
                        sx={readonlyInputSx}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label="Notes"
                        fullWidth
                        multiline
                        rows={3}
                        value={(todo as any)?.note?.message || (todo as any)?.note || ''}
                        InputProps={{ readOnly: true }}
                        sx={readonlyInputSx}
                    />
                </Grid>
            </Grid>
        </Paper>
    );
};

export default GetSingleTodo;
